import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import Nav from "./Nav";
import Footer from "./Footer";
import { AUDIT_LIST } from "./audits";
import { formatLocalPrice, useCurrency } from "./currency";
import { CurrencySwitcher, PriceNote } from "./CurrencySwitcher";
import useSeo from "./useSeo";

const ROWS = [
  ["Best for", (a) => a.bestFor],
  ["Key focus areas", (a) => a.included.slice(2, 6).map(([, t]) => t).join(" · ")],
  ["Checks covered", (a) => `${a.included.length} areas`],
  ["Free Health Check", (a, c) => formatLocalPrice(a.tiers[0].usd, c)],
  ["Account Audit", (a, c) => formatLocalPrice(a.tiers[1].usd, c)],
  ["Audit + Strategy Session", (a, c) => formatLocalPrice(a.tiers[2].usd, c)],
  ["Delivery", () => "3-5 business days"],
];

const PlatformHead = ({ a }) => (
  <div className="flex items-center gap-2">
    <span className="w-2.5 h-2.5" style={{ background: a.accent }} />
    <span className="font-heading font-black text-xl md:text-2xl tracking-tight">{a.platform}</span>
  </div>
);

const MobileCard = ({ a, i, currency }) => (
  <motion.article
    initial={{ opacity: 0, y: 14 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.4, delay: i * 0.05 }}
    className="border border-ink p-6"
    style={{ borderTop: `4px solid ${a.accent}` }}
    data-testid={`audits-card-${a.key}`}
  >
    <PlatformHead a={a} />
    <dl className="mt-5 divide-y divide-line">
      {ROWS.map(([label, fn]) => (
        <div key={label} className="py-3 grid grid-cols-5 gap-3">
          <dt className="col-span-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink2">{label}</dt>
          <dd className="col-span-3 text-[14px] text-ink">{fn(a, currency)}</dd>
        </div>
      ))}
    </dl>
    <Link to={a.path} data-testid={`audits-mobile-cta-${a.key}`} className="mt-5 w-full inline-flex justify-center items-center gap-2 bg-ink text-paper hover:bg-signal px-5 py-4 font-heading font-bold text-[12px] uppercase tracking-[0.2em] transition-colors">
      View {a.short} audit <ArrowUpRight size={14} />
    </Link>
  </motion.article>
);

const CompareTable = ({ currency }) => (
  <div className="hidden lg:block border border-ink" data-testid="audits-compare-table">
    <div className="grid grid-cols-[220px_repeat(4,1fr)] border-b border-ink">
      <div className="p-4 flex flex-col gap-2 justify-end">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">Compare · currency</span>
        <CurrencySwitcher testId="audits-currency-switcher" />
      </div>
      {AUDIT_LIST.map((a) => (
        <div key={a.key} className="p-5 border-l border-ink" style={{ borderTop: `4px solid ${a.accent}` }}>
          <PlatformHead a={a} />
        </div>
      ))}
    </div>
    {ROWS.map(([label, fn]) => (
      <div key={label} className="grid grid-cols-[220px_repeat(4,1fr)] border-b border-line last:border-b-0">
        <div className="p-5 font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">{label}</div>
        {AUDIT_LIST.map((a) => (
          <div key={a.key} className="p-5 border-l border-line text-[14px] leading-relaxed" data-testid={`audits-cell-${a.key}-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            {fn(a, currency)}
          </div>
        ))}
      </div>
    ))}
    <div className="grid grid-cols-[220px_repeat(4,1fr)] border-t border-ink">
      <div className="p-5" />
      {AUDIT_LIST.map((a) => (
        <div key={a.key} className="p-5 border-l border-ink">
          <Link to={a.path} data-testid={`audits-cta-${a.key}`} className="w-full inline-flex justify-center items-center gap-2 bg-ink text-paper hover:bg-signal px-4 py-4 font-heading font-bold text-[12px] uppercase tracking-[0.2em] transition-colors">
            View {a.short} audit <ArrowUpRight size={14} />
          </Link>
        </div>
      ))}
    </div>
  </div>
);

export default function AuditsOverview() {
  const { currency } = useCurrency();
  useSeo(
    "Paid Media Account Audits — LinkedIn, Google, Meta & Bing | The Digital Marketer",
    "Compare LinkedIn Ads, Google Ads, Meta Ads and Bing Ads account audits side by side — scope, focus areas, pricing and delivery."
  );
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="audits-overview-page">
      <Nav />
      <section className="pt-[120px] md:pt-[160px] pb-10 md:pb-14">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="font-mono text-[11px] md:text-xs uppercase tracking-[0.22em] text-signal mb-6">◆ Account audits · 4 platforms</p>
          <h1 data-testid="audits-heading" className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest max-w-4xl">
            Pick the platform. <span className="text-signal">Find the waste.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-ink2 leading-relaxed max-w-2xl">
            Every audit follows the same method — structure, targeting, creative, tracking and spend —
            tuned to how each platform actually works. Compare scope and pricing, then pick the account
            that needs it most.
          </p>
          <ul className="mt-6 flex flex-wrap gap-x-8 gap-y-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink2">
            {["Free health check on every platform", "Prioritized action plan", "Prices in your local currency, billed in USD"].map((t) => (
              <li key={t} className="flex items-center gap-2"><Check size={12} className="text-signal" /> {t}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="mb-5 flex flex-col sm:flex-row sm:items-center lg:justify-end justify-between gap-3">
            <div className="lg:hidden"><CurrencySwitcher testId="audits-mobile-currency-switcher" /></div>
            <PriceNote />
          </div>
          <CompareTable currency={currency} />
          <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-6">
            {AUDIT_LIST.map((a, i) => <MobileCard key={a.key} a={a} i={i} currency={currency} />)}
          </div>
          <div className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-6 border border-ink bg-surface p-6 md:p-8">
            <div>
              <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight">Running more than one platform?</h2>
              <p className="mt-2 text-ink2 text-base">Tell me which accounts you run and I&apos;ll recommend where to start.</p>
            </div>
            <Link to={{ pathname: "/", hash: "contact", search: "?from=/audits" }} data-testid="audits-contact-cta" className="inline-flex items-center gap-3 bg-ink text-paper hover:bg-signal px-7 py-4 font-heading font-bold text-[13px] uppercase tracking-[0.2em] transition-colors shrink-0">
              Ask which audit fits <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
